/**
 * useGoneListingSimilar Hook
 *
 * Live listings like one that is gone, for the product page's "no longer
 * available" state. Partner widgets keep linking listings for days after
 * Vendoo (or the seller) takes them down; this gives that reader somewhere to
 * go instead of a dead end. `gone=1` lets the server read the archived listing
 * as a query only -- the listing itself never reaches the page.
 */

import { useEffect, useState } from 'react';
import type { Product } from '../contexts/ProductProvider';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import { encodeAffinityParam } from '../utils/affinity-param';
import { sessionAffinityTags } from '../utils/session-interest';
import { getAnonymousId } from '../utils/tracking-context';
import { isEdgeRefusal, isForeignRefusal, isNetworkFailure, parseJsonResponse } from '../utils/read-json-response';
import { appendPageLoadAt, appendShuffleSeed } from '../utils/seeded-shuffle';
import { asError } from './as-error';

/** Enough for four rows of the browse grid on desktop. */
export const GONE_SIMILAR_LIMIT = 24;

interface SimilarListingsPage {
  success?: boolean;
  error?: string;
  listings?: Product[];
}

export function useGoneListingSimilar(listingId: string | null): { listings: Product[]; isLoading: boolean } {
  // Keyed by id, so a new id reads as loading without a reset in the effect.
  const [result, setResult] = useState<{ id: string; listings: Product[] } | null>(null);

  useEffect(() => {
    if (!listingId) return;
    let cancelled = false;

    const params = new URLSearchParams({ listingId, limit: String(GONE_SIMILAR_LIMIT), gone: '1' });
    const affinity = encodeAffinityParam(sessionAffinityTags());
    if (affinity) params.append('affinity', affinity);
    const anonymousId = getAnonymousId();
    if (anonymousId) params.append('anonymousId', anonymousId);

    (async () => {
      let listings: Product[] = [];
      try {
        const response = await fetch(`/api/search/similar?${appendPageLoadAt(appendShuffleSeed(params))}`);
        const data = await parseJsonResponse<SimilarListingsPage>(response, 'GET /api/search/similar');
        // A 404 here means the row itself is gone (hard-deleted): the page
        // still offers Browse, so it is not an error.
        if (data.success) listings = data.listings ?? [];
        else if (response.status !== 404) {
          trackError(`[GoneListingSimilar] API error: ${data.error}`, ErrorSeverity.ERROR, {
            component: 'GoneListingSimilar',
            action: 'fetch-similar-listings',
            listing_id: Number(listingId),
            tags: { http_status: String(response.status) },
          });
        }
      } catch (err) {
        const severity = isForeignRefusal(err)
          ? ErrorSeverity.INFO
          : isEdgeRefusal(err) || isNetworkFailure(err)
            ? ErrorSeverity.WARNING
            : ErrorSeverity.ERROR;
        trackError(asError(err), severity, {
          component: 'GoneListingSimilar',
          action: 'fetch-similar-listings',
          listing_id: Number(listingId),
        });
      }
      if (!cancelled) setResult({ id: listingId, listings });
    })();

    return () => {
      cancelled = true;
    };
  }, [listingId]);

  const settled = result !== null && result.id === listingId;
  return { listings: settled ? result.listings : [], isLoading: listingId !== null && !settled };
}
